import { Injectable } from '@angular/core'

const MIN_SATURATION = 0.30
const MAX_LIGHTNESS  = 0.72
const MIN_LIGHTNESS  = 0.12
const CANVAS_SIZE    = 50

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = 0
  switch (max) {
    case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break
    case gn: h = (bn - rn) / d + 2; break
    case bn: h = (rn - gn) / d + 4; break
  }
  return [h * 60, s, l]
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0, g = 0, b = 0
  if (h < 60)       { r = c; g = x; b = 0 }
  else if (h < 120) { r = x; g = c; b = 0 }
  else if (h < 180) { r = 0; g = c; b = x }
  else if (h < 240) { r = 0; g = x; b = c }
  else if (h < 300) { r = x; g = 0; b = c }
  else              { r = c; g = 0; b = x }
  const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

@Injectable({ providedIn: 'root' })
export class BrandColorService {

  async applyFromUrl(url: string): Promise<void> {
    let img: HTMLImageElement
    try {
      img = await loadImage(url)
    } catch {
      return
    }

    const canvas = document.createElement('canvas')
    canvas.width  = CANVAS_SIZE
    canvas.height = CANVAS_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE)

    let data: Uint8ClampedArray
    try {
      data = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data
    } catch {
      return
    }

    let best: { s: number; h: number; l: number } | null = null
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue
      const [h, s, l] = rgbToHsl(data[i], data[i + 1], data[i + 2])
      if (s < MIN_SATURATION || l < MIN_LIGHTNESS || l > MAX_LIGHTNESS) continue
      if (!best || s > best.s) best = { h, s, l }
    }

    if (!best) return

    document.documentElement.style.setProperty('--brand-header', hslToHex(best.h, best.s, best.l))
  }
}
